import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/db';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { Card, Button, Input, Badge } from '@/components/ui/Components';
import SafeIcon from '@/common/SafeIcon';

const Users = () => {
  const { user: currentUser, impersonate } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', password: '', role: 'superadmin' });
  const [pwModal, setPwModal] = useState(null); // { id, email }
  const [newPw, setNewPw] = useState('');
  const [editModal, setEditModal] = useState(null); // { user, business }
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    const [usersData, bizData] = await Promise.all([
      api.getUsers(),
      api.getAllBusinesses(),
    ]);
    setUsers(usersData);
    setBusinesses(bizData);
  };

  const getBusiness = (businessId) => businesses.find(b => b.id === businessId);

  const openEditModal = (u) => {
    const biz = getBusiness(u.business_id);
    setEditForm({
      email: u.email,
      business_name: biz?.name || '',
      location_limit: biz?.location_limit ?? 1,
      newPassword: '',
    });
    setEditModal({ user: u, business: biz });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.createUser(createForm);
      addToast(`Felhasználó létrehozva: ${createForm.email}`, 'success');
      setShowCreate(false);
      setCreateForm({ email: '', password: '', role: 'superadmin' });
      fetchData();
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        email: editForm.email,
        business_name: editForm.business_name,
        location_limit: editForm.location_limit,
      };
      if (editForm.newPassword) payload.newPassword = editForm.newPassword;
      await api.updateUser(editModal.user.id, payload);
      addToast('Felhasználó adatai frissítve!', 'success');
      setEditModal(null);
      setEditForm({});
      fetchData();
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) { addToast('Min. 6 karakter szükséges', 'error'); return; }
    try {
      await api.setUserPassword(pwModal.id, newPw);
      addToast(`Jelszó megváltoztatva: ${pwModal.email}`, 'success');
      setPwModal(null);
      setNewPw('');
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const handleDelete = async (u) => {
    if (!confirm(`Biztosan törlöd ezt a felhasználót: ${u.email}?`)) return;
    try {
      await api.deleteUser(u.id);
      addToast('Felhasználó törölve', 'success');
      fetchData();
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const handleImpersonate = async (u) => {
    try {
      await impersonate(u.business_id);
      navigate('/admin');
    } catch (err) {
      addToast('Nem sikerült belépni: ' + err.message, 'error');
    }
  };

  const superadmins = users.filter(u => u.role === 'superadmin');
  const admins = users.filter(u => u.role === 'admin');

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-black text-gray-900">Felhasználók</h1>
          <p className="text-gray-500">SuperAdmin és Admin fiókok kezelése.</p>
        </div>
        <Button onClick={() => setShowCreate(true)}>
          <SafeIcon name="Plus" className="w-4 h-4 mr-2" /> Új felhasználó
        </Button>
      </div>

      {/* ─── Létrehozás modal ─────────────────────────────────────────── */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md p-8 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Új felhasználó</h2>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600">
                <SafeIcon name="X" className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <Input label="E-mail cím *" type="email" value={createForm.email}
                onChange={e => setCreateForm({ ...createForm, email: e.target.value })} required />
              <Input label="Jelszó * (min. 6 karakter)" type="password" value={createForm.password}
                onChange={e => setCreateForm({ ...createForm, password: e.target.value })} required />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Szerepkör</label>
                <select
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={createForm.role}
                  onChange={e => setCreateForm({ ...createForm, role: e.target.value })}
                >
                  <option value="superadmin">SuperAdmin</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Mégse</Button>
                <Button type="submit" disabled={loading}>{loading ? 'Létrehozás...' : 'Létrehozás'}</Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* ─── Szerkesztés modal (admin) ────────────────────────────────── */}
      {editModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md p-8 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold">Felhasználó szerkesztése</h2>
                <p className="text-xs text-gray-400 mt-0.5 font-mono">{editModal.user.email}</p>
              </div>
              <button onClick={() => setEditModal(null)} className="text-gray-400 hover:text-gray-600">
                <SafeIcon name="X" className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleEdit} className="space-y-4">
              <Input
                label="E-mail cím"
                type="email"
                value={editForm.email}
                onChange={e => setEditForm({ ...editForm, email: e.target.value })}
                required
              />
              {editModal.business && (
                <>
                  <Input
                    label="Cégnév"
                    value={editForm.business_name}
                    onChange={e => setEditForm({ ...editForm, business_name: e.target.value })}
                  />
                  <div>
                    <Input
                      label="Helyszín limit (csomag)"
                      type="number"
                      min="1"
                      value={editForm.location_limit}
                      onChange={e => setEditForm({ ...editForm, location_limit: e.target.value })}
                    />
                    <p className="text-xs text-gray-400 mt-1">Hány helyszínt hozhat létre a vállalkozás.</p>
                  </div>
                </>
              )}
              <Input
                label="Új jelszó (hagyd üresen ha nem változtatod)"
                type="password"
                placeholder="••••••••"
                value={editForm.newPassword}
                onChange={e => setEditForm({ ...editForm, newPassword: e.target.value })}
              />
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditModal(null)}>Mégse</Button>
                <Button type="submit" disabled={loading}>{loading ? 'Mentés...' : 'Mentés'}</Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* ─── Jelszó módosítás modal ───────────────────────────────────── */}
      {pwModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-sm p-8 shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">Jelszó módosítása</h2>
              <button onClick={() => { setPwModal(null); setNewPw(''); }} className="text-gray-400 hover:text-gray-600">
                <SafeIcon name="X" className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">{pwModal.email}</p>
            <form onSubmit={handleSetPassword} className="space-y-4">
              <Input label="Új jelszó (min. 6 karakter)" type="password" value={newPw}
                onChange={e => setNewPw(e.target.value)} required autoFocus />
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => { setPwModal(null); setNewPw(''); }}>Mégse</Button>
                <Button type="submit">Mentés</Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* ─── SuperAdmin lista ─────────────────────────────────────────── */}
      <Card className="overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <SafeIcon name="ShieldCheck" className="w-4 h-4 text-blue-600" /> SuperAdmin fiókok ({superadmins.length})
          </h3>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <tbody className="bg-white divide-y divide-gray-200">
            {superadmins.map(u => (
              <tr key={u.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm">
                      {u.email.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{u.email}</p>
                      {u.id === currentUser.id && <p className="text-xs text-blue-600">← Te vagy</p>}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4"><Badge variant="blue">SuperAdmin</Badge></td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPwModal(u)}>
                      <SafeIcon name="Lock" className="w-4 h-4 mr-1" /> Jelszó
                    </Button>
                    {u.id !== currentUser.id && (
                      <button onClick={() => handleDelete(u)} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Törlés">
                        <SafeIcon name="Trash2" className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* ─── Admin lista ──────────────────────────────────────────────── */}
      <Card className="overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <SafeIcon name="Users" className="w-4 h-4 text-gray-600" /> Felhasználói fiókok ({admins.length})
          </h3>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <tbody className="bg-white divide-y divide-gray-200">
            {admins.length === 0 && (
              <tr><td className="px-6 py-8 text-center text-gray-400 text-sm" colSpan={3}>Nincsenek admin felhasználók</td></tr>
            )}
            {admins.map(u => {
              const biz = getBusiness(u.business_id);
              return (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <p className="font-medium text-gray-900">{u.email}</p>
                    <p className="text-xs text-gray-500">{biz?.name || u.business_id}</p>
                    {biz && (
                      <p className="text-xs text-gray-400">Limit: {biz.location_limit} helyszín</p>
                    )}
                  </td>
                  <td className="px-6 py-4"><Badge variant="gray">Admin</Badge></td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleImpersonate(u)}
                        className="text-orange-600 border-orange-200 hover:bg-orange-50"
                        title="Belépés admin fiókba"
                      >
                        <SafeIcon name="LogIn" className="w-4 h-4 mr-1" /> Belépés
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openEditModal(u)}>
                        <SafeIcon name="Edit2" className="w-4 h-4 mr-1" /> Szerkesztés
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

export default Users;
